import { ApolloError } from '@apollo/client'
import { Formik, FormikProps } from 'formik'
import { difference } from 'lodash'
import { ReactElement, useState } from 'react'
import { useIntl, FormattedMessage, defineMessages, IntlShape } from 'react-intl'
import { NavLink } from 'react-router-dom'
import styled from 'styled-components'

import Card from '@10xLabs/react-web/components/Card'
import { Column } from '@10xLabs/react-web/components/Column'
// @ts-expect-error TS(2559) FIXME Remove this comment to see the full error message
import { Hr } from '@10xLabs/react-web/components/Hr'
import { Row } from '@10xLabs/react-web/components/Row'
import { safeFormatMessage } from '@10xlabs/utils'
import { Button } from 'components/v2/Button'
import { Input } from 'components/Input'
import { ErrorMessage } from 'components/v2/ErrorMessage'
import { Label } from 'components/v2/Label'
import { InputPassword } from 'components/InputPassword'
import { Checkbox } from 'components/v2/Checkbox'
import { ICompanyAutocomplete, MultiCompanyAutocomplete } from 'features/Companies/components/MultiCompanyAutocomplete'
import { verticalMessages } from 'features/Companies/components/Vertical/messages'
import { useLogin } from 'features/Login/hooks'
import { canSelectCompany, getDefaultCompaniesForForm } from 'features/Login/utils'
import { MultiRoleAutocomplete } from 'features/Roles/components/MultiRoleAutocomplete'
import { SelectPermissions } from 'features/Roles/components/SelectPermissions'
import { INewUserValues } from 'features/Users/types'
import { getPermissionIDsByRoles, isSuperAdmin } from 'features/Users/utils'
import { GTCompanyV2Vertical, GTUser } from 'gql/types.generated'
import { getUnprocessableErrors, isUniqueUsernameError } from 'utils/error'
import { generatePassword } from 'utils/string'
import { schema } from './schema'
import { SwitchWithLabel } from 'components/v2/Switch'

const MainLabel = styled(Label)<TSFixMe>`
  padding: 10px 0;
  border-bottom: 1px solid #5d5c70;
  margin-bottom: 20px;
`

const CheckboxWrapper = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: space-between;

  input {
    margin-left: 0;
  }
`

const CopyPasswordWrapper = styled.div`
  margin-top: 16px;
`

const messages = defineMessages({
  companyPlaceholder: {
    id: 'users.form.company.placeholder',
    defaultMessage: 'Enter company name'
  },
  firstNamePlaceholder: {
    id: 'users.form.firstName.placeholder',
    defaultMessage: 'First Name'
  },
  lastNamePlaceholder: {
    id: 'users.form.lastName.placeholder',
    defaultMessage: 'Last Name'
  },
  usernamePlaceholder: {
    id: 'users.form.username.placeholder',
    defaultMessage: 'Username'
  },
  emailPlaceholder: {
    id: 'users.form.email.placeholder',
    defaultMessage: 'Email'
  },
  passwordPlaceholder: {
    id: 'users.form.password.placeholder',
    defaultMessage: 'Password'
  },
  positionPlaceholder: {
    id: 'users.form.position.placeholder',
    defaultMessage: 'Title'
  },
  selectVerticalPlaceholder: {
    id: 'companies.new.form.selectVerticalFirst.placeholder',
    defaultMessage: 'Select a company vertical'
  },
  uniqueUsernameError: {
    id: 'users.form.error.uniqueUsername',
    defaultMessage: 'Username already exists'
  }
})

const getPermissionsAfterRemovingRole = (
  currentPermissionIDs: TSFixMe,
  newRoles: TSFixMe,
  oldRoles: TSFixMe
): TSFixMe => {
  const newPermissionIDsByRoles = getPermissionIDsByRoles(newRoles)
  const oldPermissionIDsByRoles = getPermissionIDsByRoles(oldRoles)
  const oldOptionalPermissionIDs = difference(currentPermissionIDs, oldPermissionIDsByRoles)
  const newOptionalPermissionIDs = difference(oldOptionalPermissionIDs, newPermissionIDsByRoles)

  return [...newPermissionIDsByRoles, ...newOptionalPermissionIDs]
}

const getPermissionsAfterAddingRole = (currentPermissionIDs: TSFixMe, newRoles: TSFixMe): TSFixMe => {
  const newPermissionIDsByRoles = getPermissionIDsByRoles(newRoles)
  const newOptionalPermissionIDs = difference(currentPermissionIDs, newPermissionIDsByRoles)

  return [...newPermissionIDsByRoles, ...newOptionalPermissionIDs]
}

const VERTICALS_LIST = [
  GTCompanyV2Vertical.Travel,
  GTCompanyV2Vertical.City,
  GTCompanyV2Vertical.Team,
  GTCompanyV2Vertical.Rental
]

interface INewUserFormProps extends FormikProps<INewUserValues> {
  error?: TSFixMe
  loading: boolean
  isEdit?: boolean
  isSuperAdmin?: boolean
}

const getUnprocessableErrorsMessage = (error: ApolloError, intl: IntlShape): (string | object)[] => {
  return getUnprocessableErrors(error).map((error) => {
    if (isUniqueUsernameError(error)) {
      return intl.formatMessage(messages.uniqueUsernameError)
    }
    return error
  })
}

const NewUserForm = ({
  values,
  touched,
  errors,
  handleChange,
  handleBlur,
  setFieldValue,
  setValues,
  handleSubmit,
  error,
  loading,
  isEdit
}: INewUserFormProps): ReactElement => {
  const intl = useIntl()
  const login = useLogin()
  const [isPasswordCopied, setIsPasswordCopied] = useState(false)

  const disabledPermissions = getPermissionIDsByRoles(values.roles)

  const handleVerticalChange = (value: React.ChangeEvent<HTMLInputElement>): void => {
    const vertical = value.target.id as GTCompanyV2Vertical
    const verticals = values.verticals.includes(vertical)
      ? values.verticals.filter((v): boolean => v !== vertical)
      : [...values.verticals, vertical]
    const companies = values.companies.filter((company): boolean => verticals.includes(company.vertical || ''))

    setValues({
      ...values,
      companies,
      verticals
    })
  }

  const copyPassword = (): void => {
    navigator.clipboard.writeText(values.password as string)
    setIsPasswordCopied(true)
  }

  const verticalIsSelected = values.verticals.length > 0
  const isUserSuperAdmin = isSuperAdmin(login.accessToken.conditions)

  return (
    <form autoComplete="off" onSubmit={handleSubmit}>
      <Card.Content noHeader>
        {canSelectCompany(login.accessToken) && (
          <Row noMargin>
            <Column width="50%">
              <Label>
                <FormattedMessage id="companyClient.vertical.label" defaultMessage="Vertical" />
              </Label>
              <CheckboxWrapper>
                {VERTICALS_LIST.map((vertical): ReactElement => {
                  return (
                    <Checkbox
                      id={vertical}
                      key={vertical}
                      name="verticals"
                      checked={values.verticals.includes(vertical)}
                      onChange={handleVerticalChange}
                    >
                      {safeFormatMessage({
                        intl,
                        key: vertical,
                        messages: verticalMessages
                      })}
                    </Checkbox>
                  )
                })}
              </CheckboxWrapper>
              {touched.verticals && errors.verticals && <ErrorMessage>{errors.verticals}</ErrorMessage>}
            </Column>
            <Column width="50%">
              <MultiCompanyAutocomplete
                id="companies"
                values={values.companies as unknown as ICompanyAutocomplete[]}
                onChange={(values: ICompanyAutocomplete[]): void => {
                  setFieldValue('companies', values)
                }}
                onBlur={handleBlur}
                placeholder={
                  !verticalIsSelected
                    ? intl.formatMessage(messages.selectVerticalPlaceholder)
                    : intl.formatMessage(messages.companyPlaceholder)
                }
                label={<FormattedMessage id="dictionary.companies" defaultMessage="Companies" />}
                verticals={values.verticals as unknown as GTCompanyV2Vertical[]}
                touched={touched.companies}
                errors={errors.companies}
                disabled={!verticalIsSelected}
                filterFn={
                  isUserSuperAdmin
                    ? (companies: ICompanyAutocomplete[]): ICompanyAutocomplete[] => {
                        return companies.filter((company) =>
                          values.companies[0]?.policy ? company.policy?.id === values.companies[0]?.policy?.id : true
                        )
                      }
                    : undefined
                }
                useCompanyPermission={isUserSuperAdmin}
              />
            </Column>
          </Row>
        )}
        <Row>
          <Column width="50%">
            <Input
              id="firstName"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.firstName}
              placeholder={intl.formatMessage(messages.firstNamePlaceholder)}
              touched={touched.firstName}
              errors={errors.firstName}
              label={<FormattedMessage id="dictionary.firstName" defaultMessage="First Name" />}
            />
          </Column>
          <Column width="50%">
            <Input
              id="lastName"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.lastName}
              placeholder={intl.formatMessage(messages.lastNamePlaceholder)}
              touched={touched.lastName}
              errors={errors.lastName}
              label={<FormattedMessage id="dictionary.lastName" defaultMessage="Last Name" />}
            />
          </Column>
        </Row>
        <Row>
          <Column width="50%">
            <Input
              id="username"
              type="username"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.username}
              placeholder={intl.formatMessage(messages.usernamePlaceholder)}
              touched={touched.username}
              errors={errors.username}
              label={<FormattedMessage id="dictionary.username" defaultMessage="Username" />}
              readOnly={isEdit}
            />
          </Column>
          <Column width="50%">
            <Input
              id="email"
              type="email"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.email}
              placeholder={intl.formatMessage(messages.emailPlaceholder)}
              touched={touched.email}
              errors={errors.email}
              label={<FormattedMessage id="user.form.email.placeholder" defaultMessage="Email" />}
              optional={!values.mfaEnabled}
            />
          </Column>
        </Row>
        {!isEdit && (
          <Row>
            <Column width="50%">
              <Label>
                <FormattedMessage id="user.form.password.generated.label" defaultMessage="Generated Password" />
              </Label>
              <InputPassword
                name="password"
                value={values.password}
                touched={touched.password}
                errors={errors.password}
              />
            </Column>
            <Column width="50%">
              <CopyPasswordWrapper>
                <Button type="button" kind="cancel" onClick={copyPassword} fullWidth>
                  {isPasswordCopied ? (
                    <FormattedMessage id="user.form.password.copied" defaultMessage="Password copied" />
                  ) : (
                    <FormattedMessage id="user.form.password.copy" defaultMessage="Copy password" />
                  )}
                </Button>
              </CopyPasswordWrapper>
            </Column>
          </Row>
        )}
        <Row>
          <Column width="50%">
            <Input
              id="position"
              type="position"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.position}
              placeholder={intl.formatMessage(messages.positionPlaceholder)}
              touched={touched.position}
              errors={errors.position}
              label={<FormattedMessage id="dictionary.position" defaultMessage="Title" />}
            />
          </Column>
          <Column width="50%">
            <MultiRoleAutocomplete
              id="roles"
              selectedRoles={values.roles}
              companyId={values.companies[0]?.id}
              onChange={(newRoles: TSFixMe): TSFixMe => {
                let newPermissionIDs
                const oldRoles = values.roles
                const hasRemovedItem = oldRoles.length > newRoles.length

                if (hasRemovedItem) {
                  newPermissionIDs = getPermissionsAfterRemovingRole(values.permissionIDs, newRoles, oldRoles)
                } else {
                  newPermissionIDs = getPermissionsAfterAddingRole(values.permissionIDs, newRoles)
                }

                setFieldValue('roles', newRoles)
                setFieldValue('permissionIDs', newPermissionIDs)
              }}
              onBlur={handleBlur}
            />
          </Column>
        </Row>
        <Row>
          <Column width="50%">
            <SwitchWithLabel
              label={<FormattedMessage id="user.form.mfaEnabled.label" defaultMessage="Enable MFA" />}
              value={values.mfaEnabled}
              onChange={(value): void => {
                setFieldValue('mfaEnabled', value)
              }}
            />
          </Column>
          <Column width="50%">
            <SwitchWithLabel
              label={
                <FormattedMessage id="user.form.passwordExpirationEnabled.label" defaultMessage="Password expiration" />
              }
              value={values.passwordExpirationEnabled}
              onChange={(value): void => {
                setFieldValue('passwordExpirationEnabled', value)
              }}
            />
          </Column>
        </Row>
        <Hr />
        <Row>
          <Column>
            <MainLabel>
              <FormattedMessage id="dictionary.permissions" defaultMessage="Permissions" />
            </MainLabel>
            {values.companies[0]?.id && (
              <div>
                <SelectPermissions
                  companyId={values.companies[0].id}
                  values={values.permissionIDs}
                  disabledPermissions={disabledPermissions}
                  onChange={(values: TSFixMe): TSFixMe => {
                    setFieldValue('permissionIDs', values)
                  }}
                />
              </div>
            )}
          </Column>
        </Row>
      </Card.Content>
      <Card.Footer>
        {error && (
          <div>
            <ErrorMessage errors={getUnprocessableErrorsMessage(error, intl)}>
              <FormattedMessage id="users.form.error" defaultMessage="There was an error while saving your user" />
            </ErrorMessage>
          </div>
        )}
        <Card.FooterActions>
          <Button as={NavLink} to="/users" kind="cancel">
            <FormattedMessage id="dictionary.cancel" defaultMessage="Cancel" />
          </Button>
          <Button type="submit" kind="primary" loading={loading}>
            {isEdit ? (
              <FormattedMessage id="dictionary.update" defaultMessage="Update" />
            ) : (
              <FormattedMessage id="dictionary.create" defaultMessage="Create" />
            )}
          </Button>
        </Card.FooterActions>
      </Card.Footer>
    </form>
  )
}

interface INewUserProps {
  user?: GTUser
  isEdit?: boolean
  onSubmit: (values: INewUserValues) => void
  error?: ApolloError
  loading?: boolean
}

export const NewUser = ({ user, isEdit = false, onSubmit, error, loading = false }: INewUserProps): ReactElement => {
  const intl = useIntl()
  const login = useLogin()

  const permissions = user?.permissions || []
  const roles = user?.roles || []

  const initialUser: INewUserValues = {
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    username: user?.username || '',
    email: user?.email || '',
    password: !isEdit ? generatePassword() : '',
    position: user?.position || '',
    verticals: user?.companies.map((company): GTCompanyV2Vertical => company.vertical as GTCompanyV2Vertical) || [],
    companies: user?.companies
      ? user.companies.map((company): ICompanyAutocomplete => {
          return {
            id: company.id,
            name: company.name,
            vertical: company.vertical as GTCompanyV2Vertical,
            policy: company.policyRole ? { id: company.policyRole.id, name: company.policyRole.name } : undefined
          }
        })
      : getDefaultCompaniesForForm(login.accessToken),
    permissionIDs: [...permissions.map(({ id }) => id), ...getPermissionIDsByRoles(roles)],
    roles,
    mfaEnabled: user?.mfaEnabled || false,
    passwordExpirationEnabled: user?.passwordExpirationEnabled || false
  }

  return (
    <Formik initialValues={initialUser} validationSchema={schema(intl, isEdit)} onSubmit={onSubmit}>
      {(props): ReactElement => {
        return <NewUserForm {...props} error={error} loading={loading} isEdit={isEdit} />
      }}
    </Formik>
  )
}
